/**
 * DITA HTML Sanitizer
 * 
 * Security utility for sanitizing HTML content before rendering
 * Removes potentially dangerous elements and attributes
 */

/**
 * List of allowed HTML tags for DITA content
 */
const ALLOWED_TAGS = new Set([
  // Text content
  'p', 'span', 'div', 'br', 'hr',
  // Headings
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  // Lists
  'ul', 'ol', 'li', 'dl', 'dt', 'dd',
  // Tables
  'table', 'thead', 'tbody', 'tfoot', 'tr', 'th', 'td', 'caption', 'colgroup', 'col',
  // Formatting
  'strong', 'em', 'b', 'i', 'u', 's', 'sup', 'sub', 'mark', 'small', 'del', 'ins',
  // Code
  'code', 'pre', 'kbd', 'samp', 'var',
  // Links and media
  'a', 'img', 'figure', 'figcaption',
  // Semantic
  'article', 'section', 'aside', 'nav', 'header', 'footer', 'main',
  // Other
  'blockquote', 'cite', 'q', 'abbr', 'address', 'time', 'details', 'summary',
]);

/**
 * List of allowed attributes (global and per-tag)
 */
const ALLOWED_ATTRIBUTES: Record<string, Set<string>> = {
  // Global attributes we allow everywhere
  // NOTE: we now allow inline "style" to preserve DITA visual formatting
  '*': new Set(['class', 'id', 'data-dita-source_id', 'data-dita-processed', 'data-dita-href', 'data-dita-filename', 'data-dita-extension', 'title', 'lang', 'dir', 'style']),
  'a': new Set(['href', 'target', 'rel', 'download']),
  'img': new Set(['src', 'alt', 'width', 'height', 'loading']),
  'table': new Set(['border', 'cellpadding', 'cellspacing']),
  'td': new Set(['colspan', 'rowspan', 'align', 'valign']),
  'th': new Set(['colspan', 'rowspan', 'align', 'valign', 'scope']),
  'ol': new Set(['start', 'type']),
  'li': new Set(['value']),
};

/**
 * Dangerous protocols that should be blocked in href/src attributes
 */
const DANGEROUS_PROTOCOLS = ['javascript:', 'data:', 'vbscript:', 'file:'];

/**
 * Checks if a URL uses a dangerous protocol
 */
function isDangerousUrl(url: string | null): boolean {
  if (!url) return false;
  const lowerUrl = url.toLowerCase().trim();
  return DANGEROUS_PROTOCOLS.some(protocol => lowerUrl.startsWith(protocol));
}

export interface PrepareDitaHtmlOptions {
  enableSanitization?: boolean;
  maxHtmlSizeMB?: number;
  maxDomNodes?: number;
}

export function prepareDitaHtml(
  rawHtml: string,
  { enableSanitization = true, maxHtmlSizeMB = 10, maxDomNodes = 10000 }: PrepareDitaHtmlOptions = {}
): string {
  const sizeValidation = validateHtmlSize(rawHtml, maxHtmlSizeMB);
  if (!sizeValidation.isValid) {
    throw new Error(sizeValidation.message || 'HTML content is too large');
  }

  const complexityValidation = validateDomComplexity(rawHtml, maxDomNodes);
  if (!complexityValidation.isValid) {
    throw new Error(complexityValidation.message || 'HTML is too complex');
  }

  return enableSanitization ? sanitizeDitaHtml(rawHtml) : rawHtml;
}

/**
 * Sanitizes an HTML string by removing dangerous tags and attributes
 * Returns the sanitized HTML string
 */
export function sanitizeDitaHtml(htmlString: string): string {
  // Create a temporary container
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = htmlString;

  // Recursively sanitize
  sanitizeNode(tempDiv);

  return tempDiv.innerHTML;
}

/**
 * Recursively sanitizes a DOM node and its children
 */
function sanitizeNode(node: Node): void {
  // If it's a text node, nothing to sanitize
  if (node.nodeType === Node.TEXT_NODE) {
    return;
  }

  // If it's an element node
  if (node.nodeType === Node.ELEMENT_NODE) {
    const element = node as HTMLElement;
    const tagName = element.tagName.toLowerCase();

    // Remove script and title tags completely
    // - script: security
    // - title: otherwise it becomes visible text when we inject full HTML documents
    // We keep <style> so that DITA CSS is preserved visually.
    if (tagName === 'script' || tagName === 'title') {
      element.remove();
      return;
    }

    // If tag is not allowed, replace with its children
    if (!ALLOWED_TAGS.has(tagName)) {
      const parent = element.parentNode;
      if (parent) {
        // Move all children to parent
        while (element.firstChild) {
          parent.insertBefore(element.firstChild, element);
        }
        element.remove();
      }
      return;
    }

    // Sanitize attributes
    const allowedForTag = ALLOWED_ATTRIBUTES[tagName] || new Set();
    const allowedGlobal = ALLOWED_ATTRIBUTES['*'];
    const attributesToRemove: string[] = [];

    for (let i = 0; i < element.attributes.length; i++) {
      const attr = element.attributes[i];
      const attrName = attr.name.toLowerCase();

      // Check if attribute is allowed
      const isAllowed = allowedGlobal.has(attrName) || allowedForTag.has(attrName);

      if (!isAllowed) {
        attributesToRemove.push(attr.name);
      } else {
        // Additional checks for specific attributes
        if (attrName === 'href' || attrName === 'src') {
          if (isDangerousUrl(attr.value)) {
            console.warn(`⚠️ Blocked dangerous URL in ${attrName}: ${attr.value}`);
            attributesToRemove.push(attr.name);
          }
        }
      }
    }

    // Remove disallowed attributes
    attributesToRemove.forEach(attrName => element.removeAttribute(attrName));

    // Sanitize children
    const children = Array.from(element.childNodes);
    children.forEach(child => sanitizeNode(child));
  }
}

/**
 * Validates HTML content size to prevent performance issues
 */
export function validateHtmlSize(htmlString: string, maxSizeInMB: number = 10): {
  isValid: boolean;
  sizeInMB: number;
  message?: string;
} {
  const sizeInBytes = new Blob([htmlString]).size;
  const sizeInMB = sizeInBytes / (1024 * 1024);

  if (sizeInMB > maxSizeInMB) {
    return {
      isValid: false,
      sizeInMB,
      message: `HTML content is too large (${sizeInMB.toFixed(2)}MB). Maximum allowed is ${maxSizeInMB}MB.`,
    };
  }

  return {
    isValid: true,
    sizeInMB,
  };
}

/**
 * Estimates DOM complexity to prevent rendering performance issues
 */
export function validateDomComplexity(htmlString: string, maxNodes: number = 10000): {
  isValid: boolean;
  nodeCount: number;
  message?: string;
} {
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = htmlString;

  const nodeCount = tempDiv.querySelectorAll('*').length;

  if (nodeCount > maxNodes) {
    return {
      isValid: false,
      nodeCount,
      message: `HTML is too complex (${nodeCount} elements). Maximum allowed is ${maxNodes} elements.`,
    };
  }

  return {
    isValid: true,
    nodeCount,
  };
}

